import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../Common/Header';
import Footer from '../Common/Footer';

const EXCHANGE_RATE = { mxn: 1, usd: 20, eur: 22 };
const CURRENCY_SYMBOL = { mxn: '$', usd: '$', eur: '€' };
const CURRENCY_LABEL = { mxn: 'MXN', usd: 'USD', eur: 'EUR' };

const FACTURA_ENDPOINT = process.env.REACT_APP_FACTURA_ENDPOINT;

const ENTERPRISE_CONTACT_EMAIL = 'customer-service@soltarc.com';

// Precios base en MXN. La conversión a USD/EUR la hace formatPrice() dividiendo
// por EXCHANGE_RATE (usd: 20, eur: 22). yearlyEquivMonth = anual / 12.
const PLANS = [
    {
        key: 'plan5',
        badge: false,
        highlighted: false,
        pricing: { monthly: 450, yearly: 2898, yearlyEquivMonth: 241.5 },
    },
    {
        key: 'plan10',
        badge: true,
        highlighted: true,
        pricing: { monthly: 630, yearly: 3798, yearlyEquivMonth: 316.5 },
    },
    {
        key: 'plan20',
        badge: false,
        highlighted: false,
        pricing: { monthly: 850, yearly: 5129, yearlyEquivMonth: 427.42 },
    },
    {
        key: 'dedicated',
        badge: false,
        highlighted: false,
        // v1: la instancia dedicada todavía no se vende self-serve (falta el ruteo
        // por tenant en archlib-backend). La tarjeta se muestra como "Próximamente":
        // sin precio, sin dropdown, sin checkout. Los `tiers` quedan dormidos para v2.
        comingSoon: true,
        // Instancia privada sin límite de proyectos: 3 tamaños de base de datos
        // (precios base en MXN). El usuario elige el tamaño con un dropdown en la
        // tarjeta y el precio se actualiza.
        tiers: [
            { memory: '1 GiB', vcpus: '1 vCPU',  disk: '15 GiB', monthly: 1152, yearly: 7156 },
            { memory: '2 GiB', vcpus: '1 vCPU',  disk: '34 GiB', monthly: 1589, yearly: 10823 },
            { memory: '4 GiB', vcpus: '2 vCPUs', disk: '56 GiB', monthly: 2256, yearly: 18102 },
        ],
    },
];

const FAQ_KEYS = ['databases', 'changePlan', 'iva', 'renewalData', 'cancel', 'dataProtection'];

function formatPrice(mxn, currency) {
    const value = mxn / EXCHANGE_RATE[currency];
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function savingsPercent(pricing) {
    if (!pricing) return 0;
    return Math.round(((pricing.monthly * 12 - pricing.yearly) / (pricing.monthly * 12)) * 100);
}

function CheckIcon() {
    return (
        <svg
            style={{ width: '18px', height: '18px', flexShrink: 0, marginTop: '2px', color: 'var(--accent)' }}
            fill="currentColor"
            viewBox="0 0 20 20"
        >
            <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
}

function FacturaModal({ isOpen, onClose, plan, billing }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        rfc: '', direccion: '', telefono: '', tipo_factura: 'Persona Física', email: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [apiError, setApiError] = useState('');

    if (!isOpen) return null;

    const change = (field) => (e) => {
        setForm({ ...form, [field]: e.target.value });
        setErrors({ ...errors, [field]: '' });
    };

    const validate = () => {
        const e = {};
        if (!form.rfc.trim()) e.rfc = t('pages.pricing.modal.errors.rfc');
        if (!form.direccion.trim()) e.direccion = t('pages.pricing.modal.errors.direccion');
        if (!form.telefono.trim()) e.telefono = t('pages.pricing.modal.errors.telefono');
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        setLoading(true);
        setApiError('');
        try {
            const res = await fetch(FACTURA_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, plan: plan?.key, periodo: billing }),
            });
            if (!res.ok) throw new Error('server error');
            setSent(true);
        } catch {
            setApiError(t('pages.pricing.modal.apiError'));
        }
        setLoading(false);
    };

    const inputSt = (err) => ({
        width: '100%', boxSizing: 'border-box', padding: '0.75rem 1rem',
        border: `1px solid ${err ? '#ef4444' : 'var(--line)'}`, borderRadius: '8px',
        fontSize: '0.9375rem', fontFamily: 'inherit', outline: 'none',
        color: 'var(--ink)', background: '#fff',
    });

    const labelSt = {
        display: 'block', fontSize: '0.875rem', fontWeight: '500',
        color: 'var(--ink)', marginBottom: '0.5rem',
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem',
        }}>
            <div style={{
                background: '#fff', borderRadius: '16px', padding: '2rem',
                width: '100%', maxWidth: '480px', position: 'relative',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto',
            }}>
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute', top: '1rem', right: '1rem', background: 'transparent',
                        border: 'none', fontSize: '1.375rem', cursor: 'pointer',
                        color: 'var(--ink-muted)', lineHeight: 1, padding: '0.25rem 0.5rem',
                    }}
                >
                    ×
                </button>

                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--ink)', margin: '0 0 0.25rem' }}>
                    {t('pages.pricing.modal.title')}
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--ink-muted)', margin: '0 0 1.5rem' }}>
                    {t('pages.pricing.modal.subtitle')}
                </p>

                {sent ? (
                    <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                        <div style={{
                            width: '52px', height: '52px', background: '#f0fdf4', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
                        }}>
                            <svg style={{ width: '26px', height: '26px', color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <p style={{ fontWeight: '600', color: 'var(--ink)', margin: '0 0 0.5rem' }}>{t('pages.pricing.modal.successTitle')}</p>
                        <p style={{ color: 'var(--ink-muted)', fontSize: '0.875rem', margin: 0 }}>
                            {t('pages.pricing.modal.successText')}
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={labelSt}>{t('pages.pricing.modal.rfcLabel')} <span style={{ color: '#ef4444' }}>*</span></label>
                            <input value={form.rfc} onChange={change('rfc')} placeholder="XAXX010101000" style={inputSt(errors.rfc)} />
                            {errors.rfc && <p style={{ color: '#ef4444', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>{errors.rfc}</p>}
                        </div>
                        <div>
                            <label style={labelSt}>{t('pages.pricing.modal.direccionLabel')} <span style={{ color: '#ef4444' }}>*</span></label>
                            <input value={form.direccion} onChange={change('direccion')} placeholder={t('pages.pricing.modal.direccionPlaceholder')} style={inputSt(errors.direccion)} />
                            {errors.direccion && <p style={{ color: '#ef4444', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>{errors.direccion}</p>}
                        </div>
                        <div>
                            <label style={labelSt}>{t('pages.pricing.modal.telefonoLabel')} <span style={{ color: '#ef4444' }}>*</span></label>
                            <input value={form.telefono} onChange={change('telefono')} placeholder="+52 55 1234 5678" style={inputSt(errors.telefono)} />
                            {errors.telefono && <p style={{ color: '#ef4444', fontSize: '0.8125rem', margin: '0.25rem 0 0' }}>{errors.telefono}</p>}
                        </div>
                        <div>
                            <label style={labelSt}>{t('pages.pricing.modal.tipoLabel')} <span style={{ color: '#ef4444' }}>*</span></label>
                            <select value={form.tipo_factura} onChange={change('tipo_factura')} style={inputSt(false)}>
                                <option value="Persona Física">{t('pages.pricing.modal.tipoFisica')}</option>
                                <option value="Persona Moral">{t('pages.pricing.modal.tipoMoral')}</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelSt}>{t('pages.pricing.modal.emailLabel')}</label>
                            <input
                                type="email" value={form.email} onChange={change('email')}
                                placeholder="tu@empresa.com" style={inputSt(false)}
                            />
                        </div>

                        {apiError && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem' }}>
                                {apiError}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                padding: '0.875rem', background: loading ? 'var(--ink-muted)' : 'var(--accent)',
                                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.9375rem',
                                fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                            }}
                        >
                            {loading ? t('pages.pricing.modal.sending') : t('pages.pricing.modal.submit')}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function PricingCard({ plan, billing, currency, navigate }) {
    const { t } = useTranslation();
    const [hovered, setHovered] = React.useState(false);
    const [tierIdx, setTierIdx] = React.useState(0);
    const btnActive = plan.highlighted || hovered;
    const isContact = !!plan.contact;
    const comingSoon = !!plan.comingSoon;

    // Precio activo: del tamaño elegido (instancia dedicada) o del plan normal.
    const pricing = plan.tiers ? plan.tiers[tierIdx] : plan.pricing;
    const equivMonth = pricing ? (plan.tiers ? pricing.yearly / 12 : pricing.yearlyEquivMonth) : 0;
    const savings = savingsPercent(pricing);

    const mainMxn = isContact ? null : (billing === 'yearly' ? pricing.yearly : pricing.monthly);
    const symbol = CURRENCY_SYMBOL[currency];
    const label = CURRENCY_LABEL[currency];

    const features = t(`pages.pricing.plans.${plan.key}.features`, { returnObjects: true });
    const featureList = Array.isArray(features) ? features : [];

    return (
        <div style={{
            position: 'relative', display: 'flex', flexDirection: 'column',
            borderRadius: '16px', border: `2px solid ${plan.highlighted ? 'var(--accent)' : 'var(--line)'}`,
            padding: '2rem', background: '#fff', boxSizing: 'border-box',
            boxShadow: plan.highlighted ? '0 8px 32px rgba(197,168,128,0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
            transform: plan.highlighted ? 'scale(1.02)' : 'none',
        }}>
            {plan.badge && (
                <div style={{
                    position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
                    background: 'var(--accent)', color: '#fff', fontSize: '0.75rem', fontWeight: '600',
                    padding: '4px 16px', borderRadius: '999px', whiteSpace: 'nowrap', letterSpacing: '0.5px',
                }}>
                    {t(`pages.pricing.plans.${plan.key}.badge`)}
                </div>
            )}

            {!isContact && !comingSoon && billing === 'yearly' && (
                <div style={{
                    position: 'absolute', top: plan.badge ? '2.5rem' : '1rem', right: '1rem',
                    background: '#f0fdf4', color: '#16a34a', fontSize: '0.75rem',
                    fontWeight: '700', padding: '3px 10px', borderRadius: '999px',
                }}>
                    {t('pages.pricing.card.save', { percent: savings })}
                </div>
            )}

            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--ink)', margin: 0 }}>
                {t(`pages.pricing.plans.${plan.key}.name`)}
            </h3>

            <div style={{ marginTop: '1rem' }}>
                {comingSoon ? (
                    <span style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--accent)', fontFamily: 'Poppins, sans-serif' }}>
                        {t('pages.pricing.card.comingSoon')}
                    </span>
                ) : isContact ? (
                    <span style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--ink)', fontFamily: 'Poppins, sans-serif' }}>
                        {t('pages.pricing.card.customQuote')}
                    </span>
                ) : (
                    <>
                        {/* Monto arriba; moneda y periodo ABAJO */}
                        <div style={{ fontSize: '2.25rem', fontWeight: '700', color: 'var(--ink)', fontFamily: 'Poppins, sans-serif', lineHeight: 1.1 }}>
                            {symbol}{formatPrice(mainMxn, currency)}
                        </div>
                        <div style={{ color: 'var(--ink-muted)', fontSize: '0.875rem', marginTop: '0.2rem' }}>
                            {label} / {billing === 'yearly' ? t('pages.pricing.card.year') : t('pages.pricing.card.month')}
                        </div>
                        {billing === 'yearly' && (
                            <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: 'var(--ink-muted)' }}>
                                {t('pages.pricing.card.equivalent', {
                                    amount: `${symbol}${formatPrice(equivMonth, currency)}`,
                                    label,
                                })}
                            </p>
                        )}
                    </>
                )}

                {/* Instancia dedicada: elige el tamaño de la base de datos (oculto en "Próximamente") */}
                {plan.tiers && !comingSoon && (
                    <div style={{ marginTop: '0.9rem' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--ink-muted)', marginBottom: '0.35rem' }}>
                            {t('pages.pricing.card.dbSizeLabel')}
                        </label>
                        <select
                            value={tierIdx}
                            onChange={(e) => setTierIdx(Number(e.target.value))}
                            style={{
                                width: '100%', padding: '0.6rem 2.25rem 0.6rem 0.75rem', borderRadius: '8px',
                                border: '1px solid var(--line)', background: '#fff', color: 'var(--ink)',
                                fontSize: '0.875rem', fontFamily: 'inherit', cursor: 'pointer',
                                appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                                backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%233F97A0' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.75rem center',
                                backgroundSize: '16px',
                            }}
                        >
                            {plan.tiers.map((tr, i) => (
                                <option key={i} value={i}>
                                    {tr.memory} · {tr.vcpus} · {tr.disk}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <p style={{ marginTop: '0.75rem', color: 'var(--ink-muted)', fontSize: '0.875rem', lineHeight: '1.6', margin: '0.75rem 0 0' }}>
                {t(`pages.pricing.plans.${plan.key}.description`)}
            </p>

            <ul style={{
                marginTop: '1.5rem', listStyle: 'none', padding: 0,
                display: 'flex', flexDirection: 'column', gap: '0.75rem', flexGrow: 1,
            }}>
                {featureList.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                        <CheckIcon />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            {!isContact && !comingSoon && (
                <div style={{
                    marginTop: '1.5rem', padding: '0.625rem 1rem', background: 'var(--bg)',
                    borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}>
                    <svg style={{ width: '14px', height: '14px', color: 'var(--accent)', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>{t('pages.pricing.card.includesIva')}</span>
                </div>
            )}

            <button
                disabled={comingSoon}
                onClick={() => {
                    if (comingSoon) return;
                    if (isContact) {
                        const subject = encodeURIComponent(t('pages.pricing.card.enterpriseMailSubject'));
                        const body = encodeURIComponent(t('pages.pricing.card.enterpriseMailBody'));
                        window.location.href = `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${subject}&body=${body}`;
                    } else {
                        navigate(`/checkout?plan=${plan.key}&currency=${currency}&billing=${billing}${plan.tiers ? `&tier=${tierIdx}` : ''}`);
                    }
                }}
                onMouseEnter={() => !comingSoon && setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={comingSoon ? {
                    marginTop: '1rem', width: '100%', padding: '0.875rem',
                    background: 'var(--bg)', color: 'var(--ink-muted)',
                    border: '2px solid var(--line)', borderRadius: '8px',
                    fontSize: '0.9375rem', fontWeight: '600', cursor: 'not-allowed',
                    fontFamily: 'inherit',
                } : {
                    marginTop: '1rem', width: '100%', padding: '0.875rem',
                    background: btnActive ? 'var(--accent)' : 'transparent',
                    color: btnActive ? '#fff' : 'var(--accent)',
                    border: '2px solid var(--accent)', borderRadius: '8px',
                    fontSize: '0.9375rem', fontWeight: '600', cursor: 'pointer',
                    fontFamily: 'inherit', transition: 'background 0.2s, color 0.2s',
                }}
            >
                {comingSoon
                    ? t('pages.pricing.card.comingSoon')
                    : isContact ? t('pages.pricing.card.contactButton') : t('pages.pricing.card.selectButton')}
            </button>

            <p style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.8125rem', color: 'var(--ink-muted)', margin: '1rem 0 0' }}>
                {t(`pages.pricing.plans.${plan.key}.ideal`)}
            </p>
        </div>
    );
}

function Pricing() {
    const { t } = useTranslation();
    const [billing, setBilling] = useState('monthly');
    const [currency, setCurrency] = useState('mxn');
    const [openFaq, setOpenFaq] = useState(null);
    const navigate = useNavigate();

    return (
        <>
            <Header />
            <div className="page-content">

                {/* Page Header */}
                <section style={{ background: 'linear-gradient(to bottom, #f5f0ea, #fff)', padding: '7rem 1.5rem 3.5rem', textAlign: 'center' }}>
                    <h1 style={{ fontSize: '2.75rem', fontWeight: '300', color: 'var(--ink)', fontFamily: "'Cormorant Garamond', serif", margin: 0 }}>
                        {t('pages.pricing.header.title')}
                    </h1>
                    <p style={{ marginTop: '1rem', fontSize: '1.125rem', color: 'var(--ink-muted)' }}>
                        {t('pages.pricing.header.subtitle')}
                    </p>
                    <p style={{ marginTop: '0.75rem', maxWidth: '42rem', margin: '0.75rem auto 0', color: 'var(--ink-muted)', fontSize: '1rem', lineHeight: '1.7' }}>
                        {t('pages.pricing.header.note')}
                    </p>
                </section>

                {/* Plans Section */}
                <section style={{ padding: '3rem 1.5rem 5rem', background: '#fff' }}>
                    <div style={{ maxWidth: '72rem', margin: '0 auto' }}>

                        {/* Billing period toggle */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                            <div style={{
                                display: 'inline-flex', background: 'var(--bg)',
                                borderRadius: '999px', padding: '4px', border: '1px solid var(--line)',
                            }}>
                                {[
                                    { key: 'monthly', label: t('pages.pricing.billing.monthly') },
                                    { key: 'yearly',  label: t('pages.pricing.billing.yearly'), suffix: t('pages.pricing.billing.yearlySuffix') },
                                ].map(({ key, label, suffix }) => (
                                    <button
                                        key={key}
                                        onClick={() => setBilling(key)}
                                        style={{
                                            padding: '0.5rem 1.5rem', borderRadius: '999px', border: 'none',
                                            background: billing === key ? 'var(--accent)' : 'transparent',
                                            color: billing === key ? '#fff' : 'var(--ink-muted)',
                                            fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer',
                                            fontFamily: 'inherit', transition: 'all 0.2s', display: 'flex',
                                            alignItems: 'center', gap: '0.5rem',
                                        }}
                                    >
                                        {label}
                                        {suffix && (
                                            <span style={{
                                                fontSize: '0.7rem', background: '#f0fdf4', color: '#16a34a',
                                                padding: '2px 8px', borderRadius: '999px',
                                                opacity: billing === key ? 1 : 0.7,
                                            }}>
                                                {suffix}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Currency toggle */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '3rem', flexWrap: 'wrap' }}>
                            {['mxn', 'usd', 'eur'].map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCurrency(c)}
                                    style={{
                                        padding: '0.4rem 1.25rem', borderRadius: '999px',
                                        border: `1.5px solid ${currency === c ? 'var(--accent)' : 'var(--line)'}`,
                                        background: currency === c ? 'var(--accent)' : 'transparent',
                                        color: currency === c ? '#fff' : 'var(--ink-muted)',
                                        fontWeight: '600', fontSize: '0.8125rem', cursor: 'pointer',
                                        fontFamily: 'inherit', transition: 'all 0.2s', letterSpacing: '0.5px',
                                    }}
                                >
                                    {CURRENCY_LABEL[c]}
                                </button>
                            ))}
                        </div>

                        {/* Pricing cards */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                            gap: '1.5rem', maxWidth: '1200px', margin: '0 auto', alignItems: 'stretch',
                        }}>
                            {PLANS.map((plan) => (
                                <PricingCard
                                    key={plan.key}
                                    plan={plan}
                                    billing={billing}
                                    currency={currency}
                                    navigate={navigate}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section style={{ padding: '4rem 1.5rem', background: 'var(--bg)' }}>
                    <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
                        <h2 style={{
                            fontSize: '2rem', fontWeight: '300', color: 'var(--ink)',
                            textAlign: 'center', marginBottom: '2.5rem',
                            fontFamily: "'Cormorant Garamond', serif",
                        }}>
                            {t('pages.pricing.faq.title')}
                        </h2>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {FAQ_KEYS.map((faqKey, i) => (
                                <div key={faqKey} style={{ background: '#fff', borderRadius: '12px', border: '1px solid var(--line)', overflow: 'hidden' }}>
                                    <button
                                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                        style={{
                                            width: '100%', padding: '1.25rem 1.5rem',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            background: 'transparent', border: 'none', cursor: 'pointer',
                                            textAlign: 'left', fontFamily: 'inherit',
                                        }}
                                    >
                                        <span style={{ fontWeight: '500', color: 'var(--ink)', fontSize: '0.9375rem' }}>
                                            {t(`pages.pricing.faq.items.${faqKey}.q`)}
                                        </span>
                                        <svg
                                            style={{
                                                width: '20px', height: '20px', color: 'var(--ink-muted)',
                                                transform: openFaq === i ? 'rotate(180deg)' : 'none',
                                                transition: 'transform 0.2s', flexShrink: 0, marginLeft: '1rem',
                                            }}
                                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {openFaq === i && (
                                        <div style={{ padding: '0 1.5rem 1.25rem' }}>
                                            <p style={{ color: 'var(--ink-muted)', fontSize: '0.9375rem', lineHeight: '1.7', margin: 0 }}>
                                                {t(`pages.pricing.faq.items.${faqKey}.a`)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

            </div>

            <Footer />
        </>
    );
}

export default Pricing;
